// Issue #256: opening a shared CV whose grant does not resolve left the CV
// workspace completely blank. `GET /api/cv/grant:<id>/profile` answers 404, the
// profile query settles with no data, and the layout renders nothing at all:
// no message, no way back. This decides what to say instead.

export type CvAccessErrorKind = 'shared' | 'own';

export type CvAccessErrorCopy = {
  titleKey: string;
  bodyKey: string;
  primaryActionKey: string;
  secondaryActionKey: string | null;
};

export function isDelegatedCvTarget(target?: string | null) {
  if (!target) return false;
  return target !== 'master';
}

/**
 * A failed load means different things to the two audiences: a delegated
 * viewer has lost access (or never had it), while an owner is looking at a
 * transient fetch failure on their own CV.
 */
export function getCvAccessErrorKind(target?: string | null): CvAccessErrorKind {
  return isDelegatedCvTarget(target) ? 'shared' : 'own';
}

export function getCvAccessErrorCopy(
  target?: string | null,
): CvAccessErrorCopy {
  if (getCvAccessErrorKind(target) === 'shared') {
    return {
      titleKey: 'cv.accessError.sharedTitle',
      bodyKey: 'cv.accessError.sharedBody',
      primaryActionKey: 'cv.accessError.retry',
      secondaryActionKey: 'cv.accessError.openMine',
    };
  }

  return {
    titleKey: 'cv.accessError.ownTitle',
    bodyKey: 'cv.accessError.ownBody',
    primaryActionKey: 'cv.accessError.retry',
    secondaryActionKey: null,
  };
}

/** True when the workspace should show the error state instead of nothing. */
export function shouldShowCvAccessError(state: {
  hasProfile: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  return !state.hasProfile && !state.isLoading && state.isError;
}
